import * as React from 'react';
import { connect } from 'react-redux';
import { Omrade } from '@/domain/omrader';
import * as selectors from '../../selectors/selectors';
import {
    MapState,
    OmraderState,
    PreferencesState,
    RootState,
    ValgtState,
    VegobjekttypeState,
} from '@/state';
import { OmraderFeatureLayer } from './layers/OmraderFeatureLayer';
import { ValgtLayer } from './layers/SelectedFeatureLayer';
import { VegnettLayer } from './layers/VegnettFeatureLayer';
import { FeaturesLayer } from './layers/VegobjekterFeatureLayer';
import { Logo } from '../info/Logo';
import { LogoButton } from './controls/LogoButton';
import { CurrentPosition } from './controls/CurrentPosition';
import { MapImageMenu } from './controls/MapImageMenu';
import { TileServiceSelection } from './controls/TileServiceSelection';
import { ZoomControls } from './controls/ZoomControls';
import { MapMoved } from './handlers/MapMovedHandler';
import { FeatureSelectionHandler } from './handlers/FeatureSelectionHandler';
import { FeatureHoverHandler } from './handlers/FeatureHoverHandler';
import { PlacenameMarkerPopup } from './overlays/PlacenameMarkerPopup';
import { RoadReferencePopup } from './overlays/RoadReferencePopup';
import ZoomToExtentHandler from './handlers/ZoomToExtentHandler';
import { Map } from 'ol';
import { createMap, MapContextValues, MapProvider } from '@/state/context/MapContext';
import { LinkQueryLayer } from './layers/LinkQueryFeatureLayer';
import { Measure } from './controls/Measure';
import { MeasurementLayer } from './layers/MeasurementLayer';
import { ContextMenu } from './handlers/ContextMenu';
import { MapMarkerLayer } from './layers/MapMarkerLayer';
import { MousePosition } from './controls/MousePosition';
import { TileServiceLayers } from './layers/TileServiceLayers';
import { NorwayBoundsConstraint } from './layers/NorwayBoundsConstraint';
import { Toolbar } from '@/components/Toolbar';
import { PolygonLayer } from './layers/PolygonLayer';
import { PropertyBoundariesLayer } from '@/components/map/layers/PropertyBoundariesLayer';
import { mapLayerToCanvasMode } from '@/components/map/map-utils/WebMapTileServices';
import { AreaStatisticsLayer } from './layers/AreaStatisticsLayer';

interface VegkartMapProps {
    mapState: MapState;
    omraderState: OmraderState;
    roadObjectTypes: VegobjekttypeState[];
    selectedVegobjekt: ValgtState;
    zoom: number;
    measuring: boolean;
    drawingPolygon: boolean;
    aerialViewActive: boolean;
    preferences: PreferencesState;
}

class OLMapContainer extends React.Component<VegkartMapProps> {
    private readonly map: Map;
    private mapDiv: HTMLDivElement | null = null;

    constructor(props: VegkartMapProps) {
        super(props);
        const { zoom, center } = this.props.mapState;
        this.map = createMap(center, zoom);
    }
    componentDidMount() {
        if (this.mapDiv) {
            this.map.setTarget(this.mapDiv);
        }
    }

    componentWillUnmount() {
        this.map.setTarget();
    }

    render() {
        const mapContext: MapContextValues = {
            map: this.map,
        };
        const areaCounters = (
            <AreaStatisticsLayer roadObjectTypes={this.props.roadObjectTypes} />
        );

        return (
            <MapProvider value={mapContext}>
                <div className="c-map">
                    {/* Canvas and Layers */}
                    <div
                        className={`c-map__canvas c-map__canvas__${
                            mapLayerToCanvasMode[this.props.mapState.mapLayer] || ''
                        }`}
                        ref={element => {
                            this.mapDiv = element;
                        }}
                        aria-label={'Kartbilde'}
                    >
                        <TileServiceLayers />
                        <NorwayBoundsConstraint />
                        {areaCounters}
                        {this.omraderLayers()}
                        <FeaturesLayer
                            featureTypes={this.props.roadObjectTypes}
                            zIndex={101}
                        />
                        <MapMarkerLayer />
                        <LinkQueryLayer />
                        <VegnettLayer />
                        {this.props.preferences.showPropertyBoundaries && (
                            <PropertyBoundariesLayer zIndex={1} />
                        )}
                        <ValgtLayer zIndex={102} />
                        {this.props.measuring && <MeasurementLayer zIndex={101} />}
                        {this.props.drawingPolygon && <PolygonLayer zIndex={102} />}
                    </div>

                    {/* Handlers */}
                    <MapMoved />
                    <ZoomToExtentHandler />
                    <ContextMenu />
                    {!this.props.measuring && <FeatureHoverHandler />}
                    {!this.props.measuring && <FeatureSelectionHandler />}
                    <RoadReferencePopup />
                    <PlacenameMarkerPopup />

                    {/* Map Controls */}
                    <Logo />
                    <div className="c-map__controls">
                        <div className="c-map__control-group t-visible-mobile">
                            <LogoButton />
                        </div>
                        <Toolbar
                            id={'mapcontrols-toolbar'}
                            className="c-map__toolbar"
                            orientation={'vertical'}
                            ariaLabel={'Kartverktøy'}
                        >
                            <CurrentPosition />
                            <TileServiceSelection />
                            <MapImageMenu />
                            <ZoomControls zoomType={'in'} />
                            <ZoomControls zoomType={'out'} />
                            <Measure />
                        </Toolbar>
                        <MousePosition />
                    </div>
                </div>
            </MapProvider>
        );
    }

    omraderLayers() {
        const { kommune, fylke, polygon } = this.props.omraderState;
        const omrader: Omrade[] = [...kommune, ...fylke, ...(polygon ? [polygon] : [])];

        return omrader
            .filter(
                (
                    area
                ): area is Omrade & {
                    vegobjekt: NonNullable<Omrade['vegobjekt']> & {
                        geometri: NonNullable<
                            NonNullable<Omrade['vegobjekt']>['geometri']
                        >;
                    };
                } => Boolean(area.vegobjekt?.geometri && area.visible)
            )
            .map(a => (
                <OmraderFeatureLayer
                    key={a.id}
                    area={a}
                    areaGeometry={a.vegobjekt.geometri}
                    isNiBLayer={this.props.aerialViewActive}
                    zoom={this.props.zoom}
                />
            ));
    }
}

const mapStateToProps = (state: RootState) => {
    return {
        mapState: selectors.stateMap(state),
        omraderState: selectors.stateOmrader(state),
        aerialViewActive: selectors.aerialViewActive(state),
        measuring: selectors.measurementActive(state),
        drawingPolygon: state.drawingPolygon,
        roadObjectTypes: selectors.stateSelectedRoadObjectTypes(state),
        selectedVegobjekt: selectors.stateValgt(state),
        zoom: selectors.stateZoom(state),
        preferences: selectors.statePreferences(state),
    };
};

export default connect(mapStateToProps)(OLMapContainer);
